import React, { useEffect, useId, useMemo, useRef, useState } from 'react'

export type StarRatingHalfValue = number // 0..5 step 0.5

type Props = {
  /** Controlled value (0..5 step 0.5). Jika diisi -> controlled */
  value?: StarRatingHalfValue
  /** Uncontrolled default value (0..5 step 0.5) */
  defaultValue?: StarRatingHalfValue

  onChange?: (val: StarRatingHalfValue) => void

  readOnly?: boolean
  disabled?: boolean

  /** Tailwind ukuran icon, contoh "w-8 h-8" */
  sizeClassName?: string

  /** Label aksesibilitas */
  ariaLabel?: string

  /** Tampilkan angka value di samping */
  showValue?: boolean

  className?: string
}

const clampToHalf = (v: number) => {
  const clamped = Math.max(0, Math.min(5, v))
  return Math.round(clamped * 2) / 2
}

export default function StarRatingHalf({
  value,
  defaultValue = 0,
  onChange,
  readOnly = false,
  disabled = false,
  sizeClassName = 'w-8 h-8',
  ariaLabel = 'Star rating',
  showValue = false,
  className = ''
}: Props) {
  const isControlled = typeof value === 'number'
  const [internal, setInternal] = useState<StarRatingHalfValue>(clampToHalf(defaultValue))
  const currentValue = isControlled ? clampToHalf(value as number) : internal

  // hover preview (null = tidak hover)
  const [hoverValue, setHoverValue] = useState<StarRatingHalfValue | null>(null)
  const displayValue = hoverValue ?? currentValue

  const groupId = useId()
  const rootRef = useRef<HTMLDivElement | null>(null)

  const stars = useMemo(() => Array.from({ length: 5 }, (_, i) => i), [])

  const commit = (next: number) => {
    if (readOnly || disabled) return
    const nextVal = clampToHalf(next)
    if (!isControlled) setInternal(nextVal)
    onChange?.(nextVal)
  }

  const getHalfFromPointer = (e: React.MouseEvent, starIndex: number) => {
    const el = e.currentTarget as HTMLElement
    const rect = el.getBoundingClientRect()
    const x = e.clientX - rect.left
    const leftHalf = x < rect.width / 2
    return leftHalf ? starIndex + 0.5 : starIndex + 1
  }

  // Keyboard support:
  // ArrowLeft / ArrowDown: -0.5
  // ArrowRight / ArrowUp: +0.5
  // Home: 0
  // End: 5
  // Space/Enter: set ke hoverValue jika ada (kalau tidak, no-op)
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (readOnly || disabled) return

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault()
        commit(currentValue - 0.5)
        break
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault()
        commit(currentValue + 0.5)
        break
      case 'Home':
        e.preventDefault()
        commit(0)
        break
      case 'End':
        e.preventDefault()
        commit(5)
        break
      case 'Enter':
      case ' ':
        if (hoverValue !== null) {
          e.preventDefault()
          commit(hoverValue)
        }
        break
      default:
        break
    }
  }

  // Reset hover ketika mouse leave dari root
  const onMouseLeaveRoot = () => setHoverValue(null)

  // Jika disabled/readOnly -> pastikan hover tidak “nyangkut”
  useEffect(() => {
    if (readOnly || disabled) setHoverValue(null)
  }, [readOnly, disabled])

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div
        ref={rootRef}
        className={`flex items-center select-none ${disabled ? 'opacity-60' : ''}`}
        role='slider'
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={5}
        aria-valuenow={currentValue}
        aria-valuetext={`${currentValue} dari 5`}
        tabIndex={readOnly || disabled ? -1 : 0}
        onKeyDown={onKeyDown}
        onMouseLeave={onMouseLeaveRoot}
      >
        {stars.map((i) => {
          const full = displayValue >= i + 1
          const half = displayValue > i && displayValue < i + 1

          return (
            <div key={`${groupId}-${i}`} className='relative'>
              {/* empty star */}
              <StarIcon className={`${sizeClassName} text-gray-300`} />

              {/* fill overlay */}
              <div
                className='absolute inset-0 overflow-hidden pointer-events-none'
                style={{ width: full ? '100%' : half ? '50%' : '0%' }}
              >
                <StarIcon className={`${sizeClassName} text-yellow-400`} />
              </div>

              {/* Interaction layer (pointer) */}
              <button
                type='button'
                disabled={disabled}
                className='absolute inset-0 cursor-pointer focus:outline-none'
                aria-label={`Set rating star ${i + 1}`}
                onMouseMove={(e) => {
                  if (readOnly || disabled) return
                  setHoverValue(clampToHalf(getHalfFromPointer(e, i)))
                }}
                onClick={(e) => {
                  const next = getHalfFromPointer(e, i)
                  commit(next)
                }}
              />
            </div>
          )
        })}
      </div>

      {showValue && (
        <span className='text-sm text-gray-600 tabular-nums'>{currentValue.toFixed(1)}</span>
      )}
    </div>
  )
}

function StarIcon({ className }: { className: string }) {
  return (
    <svg viewBox='0 0 20 20' fill='currentColor' className={className} aria-hidden='true'>
      <path d='M10 15.27l-5.18 3.04 1.4-5.99L1.64 7.9l6.1-.52L10 1.75l2.26 5.63 6.1.52-4.58 4.42 1.4 5.99L10 15.27z' />
    </svg>
  )
}
